import { useState, useEffect, useRef } from "react";
import {
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";

const Notification = ({
  message,
  type = "info",
  duration = 5000,
  onClose,
  autoClose = true,
  position = "top-right",
  setShowNotification,
  showNotification,
}) => {
  const [progress, setProgress] = useState(100);
  const progressInterval = useRef(null);
  const startTime = useRef(null);
  const remainingTime = useRef(duration);

  const positionClasses = {
    "top-right": "top-4 right-4",
    "top-left": "top-4 left-4",
    "bottom-right": "bottom-4 right-4",
    "bottom-left": "bottom-4 left-4",
    "top-center": "top-4 left-1/2 transform -translate-x-1/2",
    "bottom-center": "bottom-4 left-1/2 transform -translate-x-1/2",
  }[position];

  const iconClasses = "h-5 w-5 flex-shrink-0";

  const Icon = {
    success: <CheckCircleIcon className={iconClasses} />,
    warning: <ExclamationTriangleIcon className={iconClasses} />,
    error: <ExclamationTriangleIcon className={iconClasses} />,
    info: <InformationCircleIcon className={iconClasses} />,
  }[type];

  const bgColor = {
    success: "bg-green-500",
    warning: "bg-yellow-500",
    error: "bg-red-500",
    info: "bg-blue-500",
  }[type];

  const progressBarColor = {
    success: "bg-green-300",
    warning: "bg-yellow-300",
    error: "bg-red-300",
    info: "bg-blue-300",
  }[type];

  useEffect(() => {
    if (!autoClose) return;

    const updateProgress = () => {
      const elapsed = Date.now() - startTime.current;
      const newProgress = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(newProgress);

      if (elapsed >= duration) {
        handleClose();
      }
    };

    startTime.current = Date.now();
    progressInterval.current = setInterval(updateProgress, 50);

    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, [autoClose, duration]);

  const handleClose = () => {
    setShowNotification(false);
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
    }
    onClose?.();
  };

  if (!showNotification) return null;

  return (
    <div
      className={`fixed ${positionClasses} z-50 w-[90%] max-w-md rounded-lg shadow-lg overflow-hidden ${bgColor}`}
    >
      <div className="flex items-start justify-between p-3 text-white text-sm">
        <div className="flex items-center gap-2 flex-1">
          <span>{Icon}</span>
          <span className="leading-snug">{message}</span>
        </div>
        <button
          onClick={handleClose}
          className="text-white opacity-70 hover:opacity-100 ml-2 focus:outline-none"
          aria-label="بستن اطلاعیه"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
      <div className="w-full h-1 bg-black bg-opacity-10">
        <div
          className={`h-full ${progressBarColor} transition-all duration-50`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
};

export default Notification;
